// Longest side we send to the API. The server re-encodes every image to 1200px
// wide with sharp anyway, so this is about what travels over the wire, not
// about the quality of what ends up stored.
const MAX_DIM = 1600

// Shrink an image in the browser before uploading it. Returns the original file
// untouched whenever downscaling would not help or would lose something.
//
// ponytail: canvas + createImageBitmap, no dependency. browser-image-compression
// and friends buy nothing here — the server already owns final quality.
export async function downscaleImage(file: File): Promise<File> {
  // A canvas keeps only the first frame of an animated GIF, and the API accepts
  // GIFs and preserves their animation, so leave them alone.
  if (file.type === 'image/gif') return file

  let bitmap: ImageBitmap
  try {
    // 'from-image' applies the EXIF rotation; without it portrait phone photos
    // come out sideways, since the tag is dropped once we redraw to a canvas.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    // HEIC does not decode outside Safari. Upload it as-is — the API converts
    // HEIC to JPEG server-side.
    return file
  }

  const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height))
  if (scale === 1) {
    bitmap.close()
    return file
  }

  const canvas = document.createElement('canvas')
  canvas.width = Math.round(bitmap.width * scale)
  canvas.height = Math.round(bitmap.height * scale)
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    bitmap.close()
    return file
  }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()

  // webp rather than jpeg: it keeps alpha, so a PNG with a transparent
  // background does not come back with a black one. It is on the API's mime
  // whitelist alongside jpeg and png.
  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/webp', 0.85))
  if (!blob || blob.size >= file.size) return file

  return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.webp', { type: 'image/webp' })
}
